import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Provider } from 'react-redux'
import { store } from './store'
import theme from './theme'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { SnackbarProvider } from './components/SnackbarProvider'
import LoginPage from './pages/LoginPage'
import HomePage from './pages/HomePage'
import ProfilePage from './pages/ProfilePage'
import PurchaseContractPage from './pages/PurchaseContractPage'
import QualityControlPage from './pages/QualityControlPage'
import SaleContractPage from './pages/SaleContractPage'
import SaleControlPage from './pages/SaleControlPage'
import ProcurementPage from './pages/ProcurementPage'
import FuturesControlPage from './pages/FuturesControlPage'
import FactoryPage from './pages/factory/FactoryPage'
import MatchingContractOverviewPage from './pages/matching/MatchingContractOverviewPage'
import DoMockupPage from './pages/matching/contractOverview/DoMockupPage'
import PlaceholderPage from './pages/PlaceholderPage'
import ComingSoonPage from './pages/ComingSoonPage'
import ReportDashboardPage from './pages/ReportDashboardPage'
import PnLDashboardPage from './pages/dashboard/pnl/PnLDashboardPage'
import LongShortDashboardPage from './pages/dashboard/longShort/LongShortDashboardPage'
import InventoryDashboardPage from './pages/dashboard/inventory/InventoryDashboardPage'
import FuturesPositionPage from './pages/dashboard/futuresPosition/FuturesPositionPage'
import RiskManagementPage from './pages/dashboard/riskManagement/RiskManagementPage'
import CashflowsDashboardPage from './pages/dashboard/cashflows/CashflowsDashboardPage'
import ReportValuePage from './pages/report/ReportValuePage'
import ReportsPage from './pages/report/ReportsPage'
import MasterDataPage from './pages/MasterDataPage'
import SettingsPage from './pages/SettingsPage'

// Purchase module pages (synced)
import LocalPListPage from './pages/purchase/LocalPListPage'
import LocalPDetailPage from './pages/purchase/LocalPDetailPage'
import LocalPPlansPage from './pages/purchase/LocalPPlansPage'
import ContractsAllListPage from './pages/purchase/ContractsAllListPage'
import NPEContractListPage from './pages/purchase/NPEContractListPage'
import NPEContractDetailPage from './pages/purchase/NPEContractDetailPage'
import AnnexListPage from './pages/purchase/AnnexListPage'
import PTBFFixationListPage from './pages/purchase/PTBFFixationListPage'
import PTBFRollingListPage from './pages/purchase/PTBFRollingListPage'
import NPEFixationListPage from './pages/purchase/NPEFixationListPage'
import NPEAllocationListPage from './pages/purchase/NPEAllocationListPage'
import NPEAllocationWizard from './pages/purchase/NPEAllocationWizard'
import CSFixationListPage from './pages/purchase/CSFixationListPage'
import ForwardListPage from './pages/purchase/ForwardListPage'

// Vendors + Refs
import VendorsListPage from './pages/vendors/VendorsListPage'
import VendorDetailPage from './pages/vendors/VendorDetailPage'
import RefsHubPage from './pages/refs/RefsHubPage'
import BanksPage from './pages/refs/BanksPage'
import CalendarsPage from './pages/refs/CalendarsPage'
import CertificationsPage from './pages/refs/CertificationsPage'
import DeliveryPlacesPage from './pages/refs/DeliveryPlacesPage'
import DeliveryTermsPage from './pages/refs/DeliveryTermsPage'
import LicensesAdminPage from './pages/refs/LicensesAdminPage'
import PackagesPage from './pages/refs/PackagesPage'
import QualitiesPage from './pages/refs/QualitiesPage'
import AdjustLotsPage from './pages/refs/AdjustLotsPage'
import UpdatesPage from './pages/refs/UpdatesPage'

/**
 * Redirect legacy `/factory/<code>/<rest>` → `/<code>/<rest>` để giữ
 * backward-compat cho bookmark/link cũ sau khi drop prefix `/factory/`.
 * `replace=true` để không kẹt trong history stack.
 */
function FactoryLegacyRedirect() {
  const { factoryCode = '' } = useParams<{ factoryCode: string }>()
  const location = useLocation()
  const prefix = `/factory/${factoryCode}`
  const rest = location.pathname.startsWith(prefix)
    ? location.pathname.slice(prefix.length)
    : ''
  const target = `/${factoryCode}${rest}${location.search}${location.hash}`
  return <Navigate to={target} replace />
}

// Data router (createBrowserRouter) thay cho <BrowserRouter> để dùng được
// `useBlocker` (chặn điều hướng khi có thay đổi chưa lưu — vd BOM report).
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="home" element={<HomePage />} />
                <Route path="profile" element={<ProfilePage />} />

                {/* Purchase Contract (legacy single-page) */}
                <Route path="purchase-contract" element={<PurchaseContractPage />} />
                <Route path="purchase/contract" element={<PurchaseContractPage />} />
                <Route path="purchase/contract/*" element={<PurchaseContractPage />} />

                {/* Purchase module — synced */}
                <Route path="purchase/local-p" element={<LocalPListPage />} />
                <Route path="purchase/local-p/:contractNo" element={<LocalPDetailPage />} />
                <Route path="purchase/contracts" element={<ContractsAllListPage />} />
                <Route path="purchase/npe-contracts" element={<NPEContractListPage />} />
                <Route path="purchase/npe-contracts/:contractNo" element={<NPEContractDetailPage />} />
                <Route path="purchase/annexes" element={<AnnexListPage />} />
                <Route path="purchase/ptbf-fixations" element={<PTBFFixationListPage />} />
                <Route path="purchase/ptbf-rollings" element={<PTBFRollingListPage />} />
                <Route path="purchase/npe-fixations" element={<NPEFixationListPage />} />
                <Route path="purchase/npe-allocations" element={<NPEAllocationListPage />} />
                <Route path="purchase/npe-allocations/new" element={<NPEAllocationWizard />} />
                <Route path="purchase/cs-fixations" element={<CSFixationListPage />} />
                <Route path="purchase/forward" element={<ForwardListPage />} />
                <Route path="purchase/plans" element={<LocalPPlansPage />} />

                {/* Vendors */}
                <Route path="vendors" element={<VendorsListPage />} />
                <Route path="vendors/:code" element={<VendorDetailPage />} />

                {/* References */}
                <Route path="purchase/refs" element={<RefsHubPage />} />
                <Route path="purchase/refs/banks" element={<BanksPage />} />
                <Route path="purchase/refs/calendars" element={<CalendarsPage />} />
                <Route path="purchase/refs/certifications" element={<CertificationsPage />} />
                <Route path="purchase/refs/delivery-places" element={<DeliveryPlacesPage />} />
                <Route path="purchase/refs/delivery-terms" element={<DeliveryTermsPage />} />
                <Route path="purchase/refs/licenses" element={<LicensesAdminPage />} />
                <Route path="purchase/refs/packages" element={<PackagesPage />} />
                <Route path="purchase/refs/qualities" element={<QualitiesPage />} />
                <Route path="purchase/refs/adjust-lots" element={<AdjustLotsPage />} />
                <Route path="purchase/refs/updates" element={<UpdatesPage />} />

                {/* Report / Dashboard */}
                {/* PnL "Commercial Position" — UI build (dummy data) theo plan/PBI_Dashboard.md.
                    Đặt TRƯỚC catch-all `report/*` (Power BI embed) để ưu tiên. */}
                {/* Dashboards — parent shell + in-page tab nav (mirror SaleContractPage). */}
                <Route path="report/dashboards" element={<ReportsPage />}>
                  <Route index element={<Navigate to="/report/dashboards/pnl" replace />} />
                  <Route path="pnl" element={null} />
                  <Route path="futures-position" element={null} />
                  <Route path="risk-management" element={null} />
                  <Route path="long-short" element={null} />
                  <Route path="inventory" element={null} />
                  <Route path="cashflows" element={null} />
                </Route>
                {/* Inputs — Navigation container (frmReportMain). */}
                <Route path="report/inputs" element={<Navigate to="/report/inputs/exposure" replace />} />
                <Route path="report/inputs/:tab" element={<ReportValuePage />} />
                <Route path="report/embed/*" element={<ReportDashboardPage />} />
                {/* /report → Dashboards; legacy flat `/report/<x>` + `/dashboard/*` redirect. */}
                <Route path="report" element={<Navigate to="/report/dashboards/pnl" replace />} />
                <Route path="report/pnl" element={<Navigate to="/report/dashboards/pnl" replace />} />
                <Route path="report/futures-position" element={<Navigate to="/report/dashboards/futures-position" replace />} />
                <Route path="report/risk-management" element={<Navigate to="/report/dashboards/risk-management" replace />} />
                <Route path="report/long-short" element={<Navigate to="/report/dashboards/long-short" replace />} />
                <Route path="report/inventory" element={<Navigate to="/report/dashboards/inventory" replace />} />
                <Route path="report/cashflows" element={<Navigate to="/report/dashboards/cashflows" replace />} />
                <Route path="dashboard/*" element={<Navigate to="/report/dashboards/pnl" replace />} />

                {/* Sales */}
                <Route path="sales/contract" element={<SaleContractPage />}>
                  <Route index element={<Navigate to="/sales/contract/list" replace />} />
                  <Route path="list" element={null} />
                  <Route path="consignment" element={null} />
                  <Route path="amendment" element={null} />
                  <Route path="ptbf-fixation" element={null} />
                  <Route path="references" element={null} />
                </Route>
                <Route path="sales/control" element={<SaleControlPage />} />
                <Route path="sales/control/*" element={<SaleControlPage />} />

                {/* Procurement */}
                <Route path="procurement" element={<ProcurementPage />} />
                <Route path="procurement/*" element={<ProcurementPage />} />

                {/* Futures Control */}
                <Route path="futures" element={<FuturesControlPage />} />
                <Route path="futures/*" element={<FuturesControlPage />} />

                {/* Factory — URL pattern `/<factoryCode>/...` (KHÔNG còn prefix `/factory/`).
                    Enumerate explicit factory codes để tránh `:slug/*` catch-all bắt nhầm
                    typo URL (vd `/abouttypo`) hay future top-level route. Thêm factory mới
                    → add 2 dòng (literal + wildcard) ở dưới. */}
                {/* Legacy redirects giữ backward-compat cho bookmark/link cũ. */}
                <Route path="factory/bao-loc" element={<Navigate to="/baoloc" replace />} />
                <Route path="factory/bao-loc/*" element={<Navigate to="/baoloc" replace />} />
                <Route path="factory/summary" element={<ComingSoonPage />} />
                <Route path="factory/:factoryCode" element={<FactoryLegacyRedirect />} />
                <Route path="factory/:factoryCode/*" element={<FactoryLegacyRedirect />} />
                <Route path="factory" element={<PlaceholderPage />} />

                {/* Bảo Lộc */}
                <Route path="baoloc" element={<FactoryPage />} />
                <Route path="baoloc/*" element={<FactoryPage />} />
                {/* Nhơn Trạch */}
                <Route path="nhontrach" element={<FactoryPage />} />
                <Route path="nhontrach/*" element={<FactoryPage />} />
                {/* An Phước (legacy DB code `nguyencuong`) */}
                <Route path="nguyencuong" element={<FactoryPage />} />
                <Route path="nguyencuong/*" element={<FactoryPage />} />
                {/* Nông Sản */}
                <Route path="nongsan" element={<FactoryPage />} />
                <Route path="nongsan/*" element={<FactoryPage />} />
                {/* Pacorini */}
                <Route path="pacorini" element={<FactoryPage />} />
                <Route path="pacorini/*" element={<FactoryPage />} />

                {/* Matching P - S — mirror Access form `frmMatchingPSMain` */}
                {/* MOCKUP — Create DO from Plan (review feedback chị Đẹp 2026-06-04;
                    gỡ route + DoMockupPage.tsx sau khi chốt). Đặt TRƯỚC :tab để
                    không bị match nhầm thành tab. */}
                <Route path="matching/do-mockup" element={<DoMockupPage />} />
                <Route path="matching" element={<MatchingContractOverviewPage />} />
                <Route path="matching/:tab" element={<MatchingContractOverviewPage />} />

                {/* Quality */}
                <Route path="quality" element={<QualityControlPage />} />
                <Route path="quality/*" element={<QualityControlPage />} />

                {/* Virtual Warehouse — all tabs parked behind ComingSoonPage until rebuild lands.
                    Title is auto-derived from the nav breadcrumb (Received / Quality Discount /
                    Delivery Note Print / Receive Note Print). Restore by swapping the element
                    back to a dedicated page that mounts the real views. */}
                <Route path="virtual-warehouse/*" element={<ComingSoonPage status="to-build" />} />


                {/* Master Data — single tabbed page (frmMdMain). MasterDataPage
                    drives top tab + inner tab + "Go to Page" Select from URL. */}
                <Route path="master-data/*" element={<MasterDataPage />} />

                {/* Settings */}
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/*" element={<SettingsPage />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Route>
    </>,
  ),
  // future của createBrowserRouter chỉ nhận data-router flags (v7_relativeSplatPath…);
  // v7_startTransition là flag của <RouterProvider> (đặt ở dưới).
  { future: { v7_relativeSplatPath: true } },
)

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <SnackbarProvider>
        <Box sx={{ height: '100%', width: '100%', margin: 0, padding: 0 }}>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </Box>
        </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  )
}

export default App
